import { ImageType, ImageVariant, NestedHome, NestedProject, ProjectImageType } from "@habity/types";

/**
 * Cloudflare flexible variant transformations
 *
 * format=auto: Cloudflare automatically serves AVIF, WebP, or JPEG based on browser Accept header
 * - AVIF: Best compression, supported by ~70% of browsers (Chrome, Firefox, Edge)
 * - WebP: Good compression, fallback for older browsers
 * - JPEG: Universal fallback
 *
 * quality=95: High quality for critical image fidelity
 * Note: AVIF at quality=95 is still smaller than JPEG at quality=85
 */
const FLEXIBLE_VARIANT_MAP: Record<ImageVariant, string> = {
  list: "w=620,h=620,fit=cover,quality=95,format=auto",
  "profile-0": "w=1529,h=682,fit=cover,quality=90,format=auto",
  "profile-1": "w=1016,h=682,fit=cover,quality=90,format=auto",
  mobile: "w=1284,h=2778,fit=cover,quality=90,format=auto",
  openGraph: "w=1200,h=630,fit=cover,quality=90,format=auto",
  thumbnail: "w=128,h=128,fit=cover,quality=85,format=auto",
  full: "w=1200,h=1600,fit=contain,quality=95,format=auto",
  "full-mobile": "w=450,fit=contain,quality=95,format=auto",
  profile: "w=620,h=620,fit=cover,quality=95,format=auto",
};

export const getImageUrl = (
  image: ImageType | ProjectImageType,
  variant: ImageVariant
): string => {
  if (!image.cloudflare_image_id) {
    console.warn("Image has no cloudflare_image_id");
    return "NO-IMAGE";
  }

  const accountHash = process.env.NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_HASH;

  if (!accountHash) {
    console.warn("NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_HASH not set");
    return "NO-IMAGE";
  }

  const flexibleVariant = FLEXIBLE_VARIANT_MAP[variant] || "public";
  return `https://imagedelivery.net/${accountHash}/${image.cloudflare_image_id}/${flexibleVariant}`;
};

export const getFilteredImages = (home: NestedHome) => {
  const { images: imagesWithNullValues, provider, status } = home || {};
  const images = imagesWithNullValues?.filter(
    (image: ImageType) => image !== null
  );
  if (!images) return [];

  let filteredImages: ImageType[] = [];

  if (provider === "vitec") {
    filteredImages = images
      ?.filter((image: ImageType) => ["vitec", "user"].includes(image.source))
      .sort(sortByVitecSortOrderKey);
  } else if (
    images.filter((image: ImageType) => image.source !== "streetview").length >
    0
  ) {
    filteredImages = images
      ?.filter((image: ImageType) => image.source !== "streetview")
      .sort(sortByImageId);
  } else {
    filteredImages = images.filter(
      (image: ImageType) => image.uploaded && !image.is_hidden
    );
  }

  // Limit images to maximum 10 for sold homes
  if (status?.name === "sold" && filteredImages.length > 10) {
    filteredImages = filteredImages.slice(0, 10);
  }

  return filteredImages;
};

const sortByImageId = (a: ImageType, b: ImageType) => {
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
};

// const sortByListOfCustomIds =
//   (ids?: (string | undefined)[] | undefined) =>
//   (a: ImageType, b: ImageType) => {
//     if (!ids || !a.vitec_image_id || !b.vitec_image_id) return 0;
//     const aIndex = ids.indexOf(a.vitec_image_id);
//     const bIndex = ids.indexOf(b.vitec_image_id);
//     if (aIndex < bIndex) return -1;
//     if (aIndex > bIndex) return 1;
//     return 0;
//   };

const sortByVitecSortOrderKey = (a: ImageType, b: ImageType) => {
  if (!a.provider_sort_order || !b.provider_sort_order) return 0;
  if (a.provider_sort_order < b.provider_sort_order) return -1;
  if (a.provider_sort_order > b.provider_sort_order) return 1;
  return 0;
};

export const getFilteredProjectImages = (project: NestedProject) => {
  const { images: imagesWithNullValues, provider } = project || {};
  const images = imagesWithNullValues?.filter(
    (image: ProjectImageType) => image !== null
  );
  if (!images) return [];

  let filteredImages: ProjectImageType[] = [];

  if (provider === "vitec") {
    filteredImages = images
      ?.filter((image: ProjectImageType) => image.uploaded && !image.is_hidden)
      .sort(sortByVitecProjectSortOrderKey);
  } else {
    filteredImages = images
      ?.filter((image: ProjectImageType) => image.uploaded && !image.is_hidden)
      .sort(sortByProjectImageId);
  }

  return filteredImages;
};

const sortByProjectImageId = (a: ProjectImageType, b: ProjectImageType) => {
  if (a.id < b.id) return -1;
  if (a.id > b.id) return 1;
  return 0;
};

const sortByVitecProjectSortOrderKey = (a: ProjectImageType, b: ProjectImageType) => {
  if (!a.provider_sort_order || !b.provider_sort_order) return 0;
  if (a.provider_sort_order < b.provider_sort_order) return -1;
  if (a.provider_sort_order > b.provider_sort_order) return 1;
  return 0;
};
